import { MessageBar, MessageBarBody, makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import { QueryAggregate } from '@powerportalspro/core';
import { GridColumnPin, MainGrid, type ViewDefinition } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { ALL_CONTACTS_VIEW_ID, getAllContactsView } from './AccountAndContactCustomViews';

// The sample contacts, with saved views (`allowSavedViews`): a signed-in user saves the grid as
// they've arranged it — its filters, columns, sort, grouping, totals, layout and page size — as a
// view of their own, offered in the view picker beside the grid's. The toolbar's Settings menu
// saves a changed view, sets the user's default and opens Manage views, where they rename, copy,
// delete, hide and show their views.
//
// `savedViewsKey` names this grid, so the views saved here and the user's default and hidden views
// are this grid's own rather than shared with every contacts grid. Filtering, grouping, layouts and
// the column menu are on, for there to be something to save.

// A view of the page's own that says how the grid lays it out, as a saved view does: its page size,
// grouping, and each column pinned, hidden, totaled or titled.
const CONTACTS_BY_COMPANY_VIEW_ID = '6B1F0C2E-3D4A-4E8B-9C71-5A2D8E4F0B13';

const contactsByCompanyView = (): ViewDefinition => ({
  ...getAllContactsView(),
  id: CONTACTS_BY_COMPANY_VIEW_ID,
  displayName: 'Contacts by company',
  pageSize: 20,
  groupLevels: [{ columnName: 'parentcustomerid' }],
  columns: [
    { columnName: 'fullname', width: 200, pin: GridColumnPin.Start },
    { columnName: 'parentcustomerid', width: 200, displayLabel: 'Company' },
    { columnName: 'emailaddress1', width: 225 },
    { columnName: 'ppp_age', width: 100, aggregates: [{ aggregate: QueryAggregate.Average }] },
    { columnName: 'mobilephone', width: 200, hidden: true },
    { columnName: 'birthdate', width: 125 },
  ],
});

const STRINGS_BASE = 'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.GridDemos.GridSavedViewsMainDemo';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
});

export function GridSavedViewsMainSample() {
  const styles = useStyles();
  const t = useT();
  const contactViews = useMemo(() => [getAllContactsView(), contactsByCompanyView()], []);

  return (
    <div className={styles.root}>
      <MessageBar intent="info" layout="multiline">
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        minHeight="450px"
        maxHeight="450px"
        pageSize={10}
        viewIds={[ALL_CONTACTS_VIEW_ID, CONTACTS_BY_COMPANY_VIEW_ID]}
        customViewDefinitions={contactViews}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
        allowFiltering
        allowGrouping
        allowChangingLayout
        allowAddingColumns
        allowSavedViews
        savedViewsKey="demo-saved-views"
      />
    </div>
  );
}
